import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Bot, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Basic Q&A chatbot for DearDr.
 * Predefined questions with predefined answers — no LLM calls.
 */

const QA = [
  {
    q: "What is DearDr?",
    a: "DearDr is India's most trusted AI-Powered platform for NEET PG & INI-CET preparation — 20,000+ MCQs, 800+ mock tests, AI explanations and personalized analytics.",
  },
  {
    q: "How do I sign up?",
    a: "Click 'Get Started' in the top-right corner. Fill your name, email and password — we'll auto-generate your unique User ID (format DDR-XXXXXX) and email it to you.",
  },
  {
    q: "Is there a free trial?",
    a: "Yes! You get 7 days of full DearDr Pro access — no credit card required.",
  },
  {
    q: "How much does DearDr cost?",
    a: "₹299/month for a single exam (NEET PG or INI-CET) and ₹499/month for both. Bigger discounts on quarterly, half-yearly and annual plans. See the Pricing section below.",
  },
  {
    q: "I forgot my password. What now?",
    a: "Click 'Login' → 'Forgot password?' or visit /forgot-password. Enter your email and we'll send you a secure reset link valid for 1 hour.",
  },
  {
    q: "Where do I download the mobile app?",
    a: "Tap the Google Play button in the hero or footer. iOS App Store version is coming soon.",
  },
  {
    q: "What subjects are covered?",
    a: "All 19 NEET PG & INI-CET subjects — Medicine, Surgery, OBG, Paediatrics, Anatomy, Physiology, Biochem, Pathology, Pharmacology, Microbiology, PSM, Ophthalmology, ENT, Ortho, Radio, Anaesthesia, Psychiatry, Derma, Forensic.",
  },
  {
    q: "How does the AI Tutor work?",
    a: "The AI Tutor is available 24×7 inside the app. Ask any concept doubt in plain English and get instant, concept-first explanations plus follow-up questions.",
  },
  {
    q: "How can I contact support?",
    a: "Scroll to the Contact section at the bottom, email hello@deardr.co.in, or call +91 90000 00000. We reply within 24 hours.",
  },
];

const GREETING = "Hi 👋 I'm the DearDr assistant. Tap a question below or type your own — I'll do my best to help.";

function matchAnswer(input) {
  const q = input.toLowerCase();
  const scored = QA.map((item) => {
    const target = item.q.toLowerCase();
    const keywords = target.split(/\W+/).filter((w) => w.length > 3);
    const score = keywords.reduce((s, k) => (q.includes(k) ? s + 1 : s), 0);
    return { item, score };
  }).sort((a, b) => b.score - a.score);
  if (scored[0].score > 0) return scored[0].item.a;
  // Fallback intents
  if (/hi|hello|hey|hola/.test(q)) return "Hello! What can I help you with today?";
  if (/thank/.test(q)) return "You're welcome! Anything else I can help with?";
  if (/human|agent|support|contact/.test(q))
    return "For a human, please use the Contact form at the bottom of the page — we reply within 24 hours.";
  return "I'm not sure I understood that. Try one of the suggested questions below or ask about signup, pricing, password reset or the mobile app.";
}

export default function ChatBot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "bot", text: GREETING }]);
  const [input, setInput] = useState("");
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const sendMessage = (text) => {
    const clean = text.trim();
    if (!clean) return;
    const reply = matchAnswer(clean);
    setMessages((prev) => [
      ...prev,
      { role: "user", text: clean },
      { role: "bot", text: reply },
    ]);
    setInput("");
  };

  const onSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  return (
    <>
      {/* Floating trigger */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="chatbot-toggle"
        aria-label="Open chatbot"
        className="fixed bottom-5 right-5 z-[60] h-14 w-14 rounded-full bg-blue-600 hover:bg-blue-700 text-white shadow-xl shadow-blue-600/30 flex items-center justify-center transition-transform hover:scale-105"
      >
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </button>

      {/* Panel */}
      {open && (
        <div
          role="dialog"
          aria-label="DearDr chatbot"
          data-testid="chatbot-panel"
          className="fixed bottom-24 right-5 z-[60] w-[min(360px,calc(100vw-2.5rem))] h-[520px] bg-white rounded-3xl border border-slate-200 shadow-2xl flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-br from-blue-600 to-indigo-700 text-white px-4 py-3 flex items-center gap-3">
            <div className="h-9 w-9 rounded-full bg-white/15 flex items-center justify-center">
              <Bot className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-heading font-bold text-sm">DearDr Assistant</div>
              <div className="text-[11px] opacity-85 flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-green-400 animate-pulse" /> Online
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              aria-label="Close chatbot"
              data-testid="chatbot-close"
              className="h-8 w-8 rounded-full hover:bg-white/15 flex items-center justify-center"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-3 py-4 space-y-2 bg-slate-50/60"
            data-testid="chatbot-messages"
          >
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm leading-snug ${
                    m.role === "user"
                      ? "bg-blue-600 text-white rounded-br-sm"
                      : "bg-white text-slate-800 rounded-bl-sm border border-slate-200"
                  }`}
                >
                  {m.text}
                </div>
              </div>
            ))}
          </div>

          {/* Suggested questions */}
          <div className="px-3 pb-2 pt-1 border-t border-slate-100 bg-white">
            <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400 mb-1.5">
              Suggested
            </div>
            <div className="flex gap-1.5 overflow-x-auto pb-1">
              {QA.slice(0, 5).map((item) => (
                <button
                  key={item.q}
                  type="button"
                  onClick={() => sendMessage(item.q)}
                  data-testid={`chatbot-suggested-${item.q.substring(0, 8).replace(/\s+/g, "-").toLowerCase()}`}
                  className="shrink-0 inline-flex items-center gap-1 text-[11px] font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-full px-2.5 py-1 transition-colors"
                >
                  {item.q}
                  <ChevronRight className="h-3 w-3" />
                </button>
              ))}
            </div>
          </div>

          {/* Input */}
          <form onSubmit={onSubmit} className="flex items-center gap-2 border-t border-slate-100 px-3 py-2.5 bg-white" data-testid="chatbot-form">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about DearDr…"
              className="flex-1 h-10 rounded-full px-4 bg-slate-100 focus:bg-white border border-transparent focus:border-blue-500 focus:outline-none text-sm"
              data-testid="chatbot-input"
            />
            <Button
              type="submit"
              size="icon"
              disabled={!input.trim()}
              data-testid="chatbot-send"
              className="h-10 w-10 rounded-full bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
            >
              <Send className="h-4 w-4" />
            </Button>
          </form>
        </div>
      )}
    </>
  );
}
